/**
 * SPA stale-bundle guard.
 *
 * `__BUILD_VERSION__` is injected at build time (git short SHA, or a
 * timestamp fallback). The same value is written to /version.txt next
 * to the bundle. This helper periodically fetches /version.txt and
 * compares the served value to the value compiled into the running
 * bundle. When they diverge, a sticky "New version available — reload"
 * banner is mounted at the top of the page and the user can take the
 * refresh at a comfortable moment.
 *
 * Why we need it: VitePWA is disabled (no Workbox precache), and our
 * own /sw.js is push-only with no fetch handler. Browsers therefore
 * fetch index.html on each navigation against the no-cache header, BUT
 * already-open SPA sessions don't re-fetch index.html until the user
 * reloads — they keep running the bundle they loaded with. On a deploy,
 * the user may be looking at the page hours after we shipped the next
 * version. The polling guard closes that window.
 *
 * Triggers:
 *   - Initial check 30s after the page loads (gives the new bundle a
 *     chance to be warm in CloudFront).
 *   - Every CHECK_INTERVAL_MS while the tab is in the foreground.
 *   - Every time the page transitions from background → visible (the
 *     user just refocused the tab and is most likely to want fresh data).
 *
 * Banner is dismissable per-version via localStorage so we don't yell
 * at someone who already saw the prompt and decided to wait.
 */

const VERSION_URL = '/version.txt';
const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const FIRST_CHECK_DELAY_MS = 30 * 1000;
const DISMISSED_KEY = 'triviana.update_dismissed_for_version';

// eslint-disable-next-line no-undef — define-injected by Vite at build time.
const RUNNING_VERSION = typeof __BUILD_VERSION__ === 'string' ? __BUILD_VERSION__ : 'dev';

let _timer = null;
let _started = false;

/**
 * Boot the checker. Idempotent — calling it twice is a no-op.
 */
export function startUpdateChecker() {
  if (_started) return;
  _started = true;
  // First check on a delay so we don't race the page load.
  setTimeout(checkOnce, FIRST_CHECK_DELAY_MS);
  // Recurring foreground polls.
  _timer = setInterval(checkOnce, CHECK_INTERVAL_MS);
  // Re-check the moment the user returns to the tab. Skip the timer
  // backfill — visibilitychange already covers the "open after sleeping"
  // case which is the highest-value trigger.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void checkOnce();
    }
  });
}

async function checkOnce() {
  try {
    // Defensive cache-bust + no-store: we never want the answer cached
    // because we're explicitly polling to detect a deploy.
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!res.ok) return;
    const served = (await res.text()).trim();
    if (!served) return;
    if (served === RUNNING_VERSION) return;
    showUpdateBanner(served);
  } catch {
    // Network blip — try again next tick. Never throw to the console.
  }
}

function dismissedVersion() {
  try { return localStorage.getItem(DISMISSED_KEY) || ''; }
  catch { return ''; }
}

function rememberDismissed(version) {
  try { localStorage.setItem(DISMISSED_KEY, version); }
  catch { /* localStorage unavailable */ }
}

function showUpdateBanner(servedVersion) {
  if (dismissedVersion() === servedVersion) return;
  if (document.getElementById('update-banner')) return; // already showing

  const el = document.createElement('div');
  el.id = 'update-banner';
  el.setAttribute('role', 'alert');
  el.innerHTML = `
    <span style="font-size:13px;font-weight:600;color:#fff;">
      A new version of Triviana is available.
    </span>
    <button type="button" id="update-banner-reload"
            style="
              background:#E5C100;color:#0E0B1F;border:none;
              padding:6px 14px;border-radius:8px;font-weight:800;
              font-family:var(--font-display);font-size:13px;cursor:pointer;
              margin-left:auto;
            ">
      Refresh
    </button>
    <button type="button" id="update-banner-dismiss" aria-label="Dismiss"
            style="
              background:transparent;border:none;color:rgba(255,255,255,0.65);
              font-size:18px;line-height:1;cursor:pointer;padding:4px 6px;
              margin-left:6px;
            ">
      ×
    </button>
  `;
  // Inline styles so the banner doesn't depend on any class load order.
  el.style.cssText = `
    position:fixed;left:50%;top:env(safe-area-inset-top,0);
    transform:translateX(-50%);z-index:100;
    width:100%;max-width:400px;
    display:flex;align-items:center;gap:8px;
    padding:10px 12px;
    background:linear-gradient(180deg,#3A2670 0%,#221547 100%);
    border:1px solid rgba(229,193,0,0.55);
    border-top-left-radius:0;border-top-right-radius:0;
    border-bottom-left-radius:12px;border-bottom-right-radius:12px;
    box-shadow:0 4px 18px rgba(0,0,0,0.45);
  `;
  document.body.appendChild(el);

  document.getElementById('update-banner-reload')?.addEventListener('click', () => {
    // Hard reload bypasses bfcache. We don't trust soft reload here
    // because the served version is freshly different — we want a clean
    // re-fetch of index.html.
    window.location.reload();
  });
  document.getElementById('update-banner-dismiss')?.addEventListener('click', () => {
    rememberDismissed(servedVersion);
    el.remove();
  });
}
